import { Button, Tooltip } from '@mantine/core';
import { ListTodo, Plus } from 'lucide-react';

import { useBookingTaskContext } from '@/hooks/use-booking-task-context.ts';

import { TaskModalMode } from '@/models/TaskModalMode.ts';
import { AppointmentBookStatus } from '@/models/appointment/Appointment.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { MULTIPLE_BOOKINGS_UNAVAILABLE_TEXT } from '@/utils/practitioner/bookingFormOptionsUtils.ts';
import { buildBookingTaskPrefill } from '@/utils/task-utils.ts';
import { openBookingTaskModal } from '@/utils/task-utils.tsx';

interface Props {
    showAddAppointment?: boolean;
}

export const BookingActionsBar = ({ showAddAppointment = false }: Props) => {
    const addBookingTask = useBookingStore((state) => state.addBookingTask);
    const addNewDefaultDraft = useBookingStore((state) => state.addNewDefaultDraft);
    const appointmentsFormDraft = useBookingStore((state) => state.appointments_form_draft);
    const isLastFormValid = useBookingStore((state) => state.is_last_form_valid);
    const isBookingLoading = useBookingStore((state) => state.is_booking_loading);
    const isSeriesMode = useBookingStore((state) => state.isSeriesMode);
    const isChainMode = useBookingStore((state) => state.is_chain_mode);
    const {
        tasksEnabled,
        bookedAppointments,
        practitionerData,
        currentPatient,
        currentForm,
        instanceId,
        currentBatch,
    } = useBookingTaskContext();

    const handleCreateTask = (): void =>
        openBookingTaskModal({
            mode: TaskModalMode.EDITOR,
            prefill: buildBookingTaskPrefill({
                bookedAppointments,
                currentPatient,
                practitionerData,
                currentForm,
                instanceId,
            }),
            onSaved: addBookingTask,
        });

    const actionsDisabled = isBookingLoading;
    const isCreatingCollection = isSeriesMode || isChainMode;
    const isAddAppointmentDisabled =
        (appointmentsFormDraft.length > 0 && !isLastFormValid) ||
        isBookingLoading ||
        isCreatingCollection ||
        !!currentBatch?.enriched_appointments.some((apt) => apt.status === AppointmentBookStatus.DECLINED);

    if (!tasksEnabled && !showAddAppointment) return undefined;
    return (
        <div className="flex w-full gap-2">
            {tasksEnabled && (
                <Button
                    leftSection={<ListTodo />}
                    className="flex-1 shadow-sm"
                    variant="outline"
                    onClick={handleCreateTask}
                    disabled={actionsDisabled}
                >
                    Aufgabe erstellen
                </Button>
            )}
            {showAddAppointment && (
                <Tooltip
                    label={MULTIPLE_BOOKINGS_UNAVAILABLE_TEXT}
                    disabled={!isCreatingCollection}
                    multiline
                    classNames={{ tooltip: 'max-w-md' }}
                >
                    {/* div wrapper so the tooltip still shows while the button itself is disabled. */}
                    <div className="flex-1">
                        <Button
                            disabled={isAddAppointmentDisabled}
                            leftSection={<Plus />}
                            className="w-full shadow-sm"
                            variant="outline"
                            onClick={() => addNewDefaultDraft()}
                        >
                            Termin hinzufügen
                        </Button>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};
